import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
} from '@mui/material';
import { FC } from 'react';
import { useInventoryState } from '../../../../contexts/inventory.context';
import { useTranslation } from 'src/hooks/useTranslationHook';

const InventoryItemDetailsView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedItem } = useInventoryState();

  if (!selectedItem) {
    return (
      <Box
        sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}
        data-testid="inventory-item-details-container"
      >
        <Typography>
          {translateText('administration.inventory.noData')}
        </Typography>
      </Box>
    );
  }

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return translateText('common.nA');
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString();
  };

  return (
    <Box
      sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}
      data-testid="inventory-item-details-container"
    >
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        {translateText('administration.inventory.columns.itemName')}:{' '}
        {selectedItem.itemName}
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText('common.description')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} data-testid="item-category">
              <Typography variant="caption" color="textSecondary">
                {translateText('administration.inventory.columns.category')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedItem.category}
              </Typography>
            </Grid>
            {selectedItem.subCategory && (
              <Grid item xs={12} sm={6} md={4} data-testid="item-subcategory">
                <Typography variant="caption" color="textSecondary">
                  {translateText(
                    'administration.inventory.columns.subCategory'
                  )}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedItem.subCategory}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4} data-testid="item-location">
              <Typography variant="caption" color="textSecondary">
                {translateText('administration.inventory.columns.location')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedItem.location}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="item-condition">
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block' }}
              >
                {translateText('administration.inventory.columns.condition')}
              </Typography>
              <Chip
                label={selectedItem.condition}
                color={
                  selectedItem.condition === 'NEW'
                    ? 'success'
                    : selectedItem.condition === 'GOOD'
                      ? 'primary'
                      : 'default'
                }
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="item-status">
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block' }}
              >
                {translateText('administration.inventory.columns.status')}
              </Typography>
              <Chip
                label={
                  selectedItem.isActive
                    ? translateText('administration.inventory.status.active')
                    : translateText('administration.inventory.status.inactive')
                }
                color={selectedItem.isActive ? 'success' : 'default'}
                size="small"
              />
            </Grid>
            {selectedItem.description && (
              <Grid item xs={12} data-testid="item-description">
                <Typography variant="caption" color="textSecondary">
                  {translateText('common.description')}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedItem.description}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText('administration.inventory.columns.quantity')} &{' '}
            {translateText('administration.inventory.columns.unitPrice')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} data-testid="item-quantity">
              <Typography variant="caption" color="textSecondary">
                {translateText('administration.inventory.columns.quantity')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedItem.quantity} {selectedItem.unit}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="item-unit-price">
              <Typography variant="caption" color="textSecondary">
                {translateText('administration.inventory.columns.unitPrice')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                ₹{selectedItem.unitPrice.toLocaleString()}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="item-total-value">
              <Typography variant="caption" color="textSecondary">
                {translateText('administration.inventory.columns.totalValue')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                ₹{selectedItem.totalValue.toLocaleString()}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="item-minimum-stock">
              <Typography variant="caption" color="textSecondary">
                Minimum Stock
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedItem.minimumStock}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="item-maximum-stock">
              <Typography variant="caption" color="textSecondary">
                Maximum Stock
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedItem.maximumStock}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="item-reorder-point">
              <Typography variant="caption" color="textSecondary">
                Reorder Point
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedItem.reorderPoint}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Supplier Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} data-testid="item-supplier">
              <Typography variant="caption" color="textSecondary">
                {translateText('administration.inventory.columns.supplier')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedItem.supplier}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} data-testid="item-supplier-contact">
              <Typography variant="caption" color="textSecondary">
                Supplier Contact
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedItem.supplierContact}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Dates & Timeline
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} data-testid="item-purchase-date">
              <Typography variant="caption" color="textSecondary">
                Purchase Date
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(selectedItem.purchaseDate)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="item-last-restocked">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.inventory.columns.lastRestocked'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(selectedItem.lastRestocked)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="item-next-restock">
              <Typography variant="caption" color="textSecondary">
                {translateText('administration.inventory.columns.nextRestock')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(selectedItem.nextRestockDate)}
              </Typography>
            </Grid>
            {selectedItem.expiryDate && (
              <Grid item xs={12} sm={6} md={4} data-testid="item-expiry-date">
                <Typography variant="caption" color="textSecondary">
                  Expiry Date
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDate(selectedItem.expiryDate)}
                </Typography>
              </Grid>
            )}
            {selectedItem.warrantyExpiryDate && (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                data-testid="item-warranty-expiry"
              >
                <Typography variant="caption" color="textSecondary">
                  Warranty Expiry
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDate(selectedItem.warrantyExpiryDate)}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {(selectedItem.assignedTo || selectedItem.notes) && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Additional Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {selectedItem.assignedTo && (
                <Grid item xs={12} sm={6} data-testid="item-assigned-to">
                  <Typography variant="caption" color="textSecondary">
                    Assigned To
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedItem.assignedTo}
                  </Typography>
                </Grid>
              )}
              {selectedItem.notes && (
                <Grid item xs={12} data-testid="item-notes">
                  <Typography variant="caption" color="textSecondary">
                    {translateText('common.remarks')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedItem.notes}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default InventoryItemDetailsView;
